<template>
  <form-layout :label="label" :required="required" :optional="optional">
    <div v-if="requiredDocumentOption === null">物件を選択してください</div>
    <div v-else>
      <radio-buttons v-model="documentTypeValue" :options="options" :invalid="!!documentTypeErrorMessage" />
      <div class="invalid-feedback" :class="documentTypeErrorMessage ? 'd-block' : ''">
        {{ documentTypeErrorMessage }}
      </div>
      <div v-show="documentTypeValue !== $constant.DOCUMENT_TYPE.ATTACHED_NONE">
        <div class="w-100" />
        <div class="form-file py-2">
          <a v-if="uploadedFileValue" :href="uploadedFileValue" class="me-3" target="_blank">
            <base-button class="btn-sm btn-light border-dark col-md-1" text="確認" />
          </a>
        </div>
        <div class="w-100">
          <span class="p-0" :class="uploadFileErrorMessage ? 'text-danger' : ''">{{ uploadFileValue?.name ? uploadFileValue?.name : fileName }}</span>
        </div>
        <div class="row row-cols-auto">
          <file-upload-common :multipleFile="false" @dataInput="handleUpload" />
          <div class="invalid-feedback" :class="uploadFileErrorMessage ? 'd-block' : ''">
            {{ uploadFileErrorMessage }}
          </div>
        </div>
        <div class="d-flex flex-column">
          <span class="text-danger">{{ this.error }}</span>
          <span v-if="uploadedFileValue" class="form-text">{{ label }}を変更する場合はファイルを選択してください</span>
        </div>
      </div>
    </div> 
  </form-layout>
</template>

<script>
import BaseVuexFormItem from '@/views/molecules/formItems/BaseVuexFormItem'
import FormLayout from "@/views/templates/FormLayout";
import RadioButtons from "@/views/atoms/inputs/RadioButtons";
import FileUploadCommon from '@/views/atoms/inputs/FileUploadCommon';
import BaseButton from '@/views/atoms/BaseButton';

export default {
  name: "VuexDocumentUploadFormItem",
  extends: BaseVuexFormItem,
  components: {
    FormLayout,
    RadioButtons,
    BaseButton,
    FileUploadCommon,
  },
  inject: [
    '$constant'
  ],
  props: {
    fileName: {
      type: String,
      default: '選択されていません'
    },
    label: {
      type: String,
      default: '依頼主確認書類'
    },
    required: {
      type: Boolean,
      default: false
    },
    optional: {
      type: Boolean,
      default: false
    },
    bind: {
      type: String,
      default: '',
    },
    bindDocumentType: {
      type: String,
      default: 'required_document_type',
    },
    bindUploadFile: {
      type: String,
      default: 'required_document_upload_file',
    },
    bindUploadedFile: {
      type: String,
      default: 'required_document_path',
    },
    isOwner: {
      type: Boolean,
      default: false
    },
    requiredDocumentOption: {
      type: [Number, Array],
      default: null
    },
  },
  data() {
    return {
      vuex_document_type_column: '',
      vuex_upload_file_column: '',
      vuex_uploaded_file_column: '',
      error: '',
    }
  },
  created() {
    if (this.bindModule) {
      this.vuex_document_type_column = this.bindDocumentType;
      this.vuex_upload_file_column = this.bindUploadFile;
      this.vuex_uploaded_file_column = this.bindUploadedFile;
    } else {
      const splitBindDocumentType = this.bindDocumentType.split('/');
      this.vuex_document_type_column = splitBindDocumentType.pop();
      this.vuex_upload_file_column = this.bindUploadFile.split('/').pop();
      this.vuex_uploaded_file_column = this.bindUploadedFile.split('/').pop();
      this.vuex_module = splitBindDocumentType.join('/');
    }
  },
  computed: {
    normalizedRequiredDocumentOption() {
      if (Array.isArray(this.requiredDocumentOption)) {
        if (this.requiredDocumentOption.includes(this.$constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN)) {
          return this.$constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN;
        }
        if (this.requiredDocumentOption.includes(this.$constant.REQUIRED_DOCUMENT_OPTIONS.NOT_REQUIRED)) {
          return this.$constant.REQUIRED_DOCUMENT_OPTIONS.NOT_REQUIRED;
        }
        return this.$constant.REQUIRED_DOCUMENT_OPTIONS.REQUIRED;
      }

      return this.requiredDocumentOption;
    },
    allowedDocumentTypes() {
      if (Array.isArray(this.requiredDocumentOption)) {
        const documentTypes = this.requiredDocumentOption.filter(value =>
          this.$constant.DOCUMENT_TYPE.LIST.some(type => type.value === value)
          && value !== this.$constant.DOCUMENT_TYPE.HIDDEN
          && value !== this.$constant.DOCUMENT_TYPE.UNNECESSARY
          && value !== this.$constant.DOCUMENT_TYPE.ATTACHED_NONE
        );

        return documentTypes.length ? documentTypes : [1,2,3];
      }

      return [1,2,3];
    },
    documentTypeValue: {
      get() {
        if (this.isOwner) {
          // 初期値として「所有者のため提出不要」を選択
          this._commit(this.vuex_document_type_column, 4);
        }
        if (this.normalizedRequiredDocumentOption === this.$constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN) {
          //初期値として「非表示」を選択
          this._commit(this.vuex_document_type_column, 0);
        }
        return this._getter(this.vuex_document_type_column);
      },
      set(value) {
        this._commit(this.vuex_document_type_column, value);
      },
    },
    uploadFileValue: {
      get() {
        if(this.documentTypeValue === this.$constant.DOCUMENT_TYPE.ATTACHED_NONE) {
          this._commit(this.vuex_upload_file_column, '');
        }
        return this._getter(this.vuex_upload_file_column);
      },
      set(value) {
        this._commit(this.vuex_upload_file_column, Object.values(value)[0]);
      },
    },
    uploadedFileValue: {
      get() {
        return this._getter(this.vuex_uploaded_file_column);
      },
      set(value) {
        this._commit(this.vuex_uploaded_file_column, value);
      },
    },
    documentTypeErrorMessage() {
      return this.$store.state[this.vuex_module]?.errors?.[this.vuex_document_type_column]?.[0];
    },
    uploadFileErrorMessage() {
      return this.$store.state[this.vuex_module]?.errors?.[this.vuex_upload_file_column]?.[0];
    },
    options() {
      let values;
      if (this.isOwner) {
        values = [4];
      } else if (this.normalizedRequiredDocumentOption === this.$constant.REQUIRED_DOCUMENT_OPTIONS.REQUIRED) {
        values = this.allowedDocumentTypes;
      } else if (this.normalizedRequiredDocumentOption === this.$constant.REQUIRED_DOCUMENT_OPTIONS.NOT_REQUIRED) {
        values = [...this.allowedDocumentTypes, 5];
      } else if (this.normalizedRequiredDocumentOption === this.$constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN) {
        values = [0];
      } else {
        values = [];
      }
      return this.$constant.DOCUMENT_TYPE.LIST.filter(type =>values.includes(type.value));
    }
  },
  methods: {
    handleUpload(payload) {
      if (payload.length === 1) {
        this.uploadFileValue = { value : payload[0] };
        this.error = null;
      } else if (payload.length === 0) {
        this.uploadFileValue = { value: null };
        this.error = null;
      } else {
        this.uploadFileValue = { value: null };
        this.error = 'ファイルは１つだけ選択してください。';
      }
    }
  },
}
</script>
